import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { AddVehicleFormComponent } from '../../../add-vehicle-form/add-vehicle-form.component';
import {
  VehicleItemComponent,
  VehicleItemData,
} from '../../../vehicle-item/vehicle-item.component';
import {
  VehicleListComponent,
  EditVehicleData,
} from '../../../vehicle-list/vehicle-list.component';
import { AlertModalComponent } from '../../../../shared/components/alert-modal/alert-modal.component';
import { VehicleService } from '../../../../services/vehicle.service';
import { NotificationService } from '../../../../services/notification.service'; // NotificationService;
import { LoggerService } from '../../../../services/logger.service';
import { VehicleData } from '../../../../shared/types';
import {
  FamilyVehicleSlot,
  VehicleFormSlot,
  AddOrderStateService,
} from '../../services/add-order-state.service';

export interface VehicleStepResult {
  familyVehicleSlots: FamilyVehicleSlot[];
  vehicleFormSlots: VehicleFormSlot[];
  hasExistingVehicles: boolean;
  hasSwitchedToFamilyPlan: boolean;
}

/**
 * Step 3 — Vehicle Details.
 * Handles loading existing customer vehicles, adding new vehicles via
 * AddVehicleFormComponent, family plan switching, editing and deleting
 * saved vehicles, and duplicate detection.
 *
 * Reuses: AddVehicleFormComponent, VehicleItemComponent, AlertModalComponent.
 */
@Component({
  selector: 'app-vehicle-step',
  standalone: true,
  imports: [
    CommonModule,
    AddVehicleFormComponent,
    VehicleItemComponent,
    VehicleListComponent,
    AlertModalComponent,
  ],
  templateUrl: './vehicle-step.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./vehicle-step.component.scss'],
})
export class VehicleStepComponent implements OnInit, OnChanges, OnDestroy {
  @Input() customerId = '';
  @Input() isFamilyPlanFromPackage = false;
  @Input() sectionTitle = 'Vehicle Details';
  /** Restore state when navigating back to this step. */
  @Input() stateService?: AddOrderStateService;

  @Output() vehiclesReady = new EventEmitter<VehicleStepResult>();
  @Output() customerFetched = new EventEmitter<any>();
  @Output() back = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();

  familyVehicleSlots: FamilyVehicleSlot[] = [];
  vehicleFormSlots: VehicleFormSlot[] = [];
  hasExistingVehicles = false;
  isFamilyPlanEnabled = false;
  hasSwitchedToFamilyPlan = false;
  familyPlanVehicleCount = 0;
  isLoading = false;

  /** Set false while waiting on first GET vehicles for a customer (prevents empty "Add Vehicle" flash). */
  initialVehiclesFetchDone = true;

  // Delete flow
  showDeleteModal = false;
  vehicleToDelete: any = null;
  vehicleToDeleteIndex: number | null = null;
  isDeletingVehicle = false;
  isDeletingToSwitchToIndividualPlan = false;

  // Edit flow
  isEditingVehicle = false;
  editingVehicleData: EditVehicleData | null = null;
  vehicleToEditIndex: number | null = null;

  // Add vehicle for existing user
  isAddingForExistingUser = false;
  newVehicleForm = {
    selectedState: '',
    licensePlate: '',
    vehicleData: null as VehicleData | null,
    isValid: false,
    isLoading: false,
    errorMessage: '',
  };

  // Family plan alert
  showFamilyPlanAlert = false;
  showFamilyPlanSuggestion = false;

  // Track open menu
  openMenuVehicleId: string | number | null = null;

  constructor(
    private readonly vehicleService: VehicleService,
    private readonly messageService: NotificationService,
    private readonly logger: LoggerService
  ) {}

  /**
   * Initializes the vehicle step by restoring saved state, setting up slots, and fetching existing vehicles when needed.
   * @example
   * gOnInit()
   * undefined
   * @returns {void} No return value.
   **/
  ngOnInit(): void {
    if (this.stateService) {
      this.restoreFromState();
    } else if (!this.customerId?.trim()) {
      this.initSlots();
    }

    if (!this.customerId?.trim()) {
      if (
        this.stateService &&
        this.vehicleFormSlots.length === 0 &&
        this.familyVehicleSlots.length === 0
      ) {
        this.initSlots();
      }
      return;
    }

    const hasRestoredVehicleUi =
      this.familyVehicleSlots.length > 0 ||
      this.vehicleFormSlots.some(s => !!s.formData?.vehicleData || !!s.formData?.vehicleId);

    if (!hasRestoredVehicleUi) {
      this.initialVehiclesFetchDone = false;
      this.vehicleFormSlots = [];
      this.hasExistingVehicles = false;
      this.familyVehicleSlots = [];
    }

    this.fetchExistingVehicles();
  }

  /**
   * Handles customerId changes by resetting vehicle state or fetching existing vehicles.
   * @example
   * gOnChanges(changes)
   * void
   * @param {SimpleChanges} changes - Angular changes object containing updated input values.
   * @returns {void} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    const idCh = changes['customerId'];
    if (!idCh || idCh.firstChange) return;

    const curr = (idCh.currentValue ?? '').toString().trim();
    const prev = (idCh.previousValue ?? '').toString().trim();
    if (curr === prev) return;

    if (!curr) {
      this.initialVehiclesFetchDone = true;
      this.hasExistingVehicles = false;
      this.familyVehicleSlots = [];
      this.initSlots();
      this.ensureSingleVehicleSelection();
      return;
    }

    this.initialVehiclesFetchDone = false;
    this.vehicleFormSlots = [];
    this.familyVehicleSlots = [];
    this.hasExistingVehicles = false;
    this.fetchExistingVehicles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Getters ────────────────────────────────────────────────────────────────

  get isSelectedPackageFamilyPlan(): boolean {
    return this.isFamilyPlanFromPackage || this.hasSwitchedToFamilyPlan;
  }

  get hasMultipleVehicles(): boolean {
    return this.vehicleFormSlots.length + this.familyVehicleSlots.length > 1;
  }

  /**
   * Determines whether at least one vehicle entry is valid for washing.
   * @example
   * isVehicleFormValid()
   * true
   * @returns {boolean} Returns true when either an existing vehicle slot or a new vehicle slot is valid and marked for wash.
   **/
  get isVehicleFormValid(): boolean {
    const hasValidExisting = this.familyVehicleSlots.some(s => s.vehicleData && s.bookForWash);
    const hasValidNew = this.vehicleFormSlots.some(s => {
      const hasFields = s.formData?.selectedState && s.formData?.licensePlate?.trim();
      const hasVehicleDetails = !!s.formData?.vehicleData;
      const isSaved = this.hasExistingVehicles ? !!s.formData?.vehicleId : true;
      return hasFields && hasVehicleDetails && isSaved && s.bookForWash;
    });
    return hasValidExisting || hasValidNew;
  }

  get hasUnsavedVehicles(): boolean {
    if (!this.hasExistingVehicles) return false;
    return this.vehicleFormSlots.some(
      s => s.vehicleData && (!s.formData.vehicleId || s.formData.vehicleId === '')
    );
  }

  get canAddMoreVehicles(): boolean {
    return this.vehicleFormSlots.length + this.familyVehicleSlots.length < 5;
  }

  get isVehicleLimitReached(): boolean {
    return !this.canAddMoreVehicles;
  }

  /** Inner subtitle under “Vehicle Details” — matches reference: show whenever customer has vehicles to pick from. */
  get headerText(): string {
    return this.hasExistingVehicles && this.familyVehicleSlots.length > 0
      ? 'Choose Vehicle'
      : 'Vehicle Details';
  }

  /** Banner row inside the white pane (subtitle and/or family-plan hint). */
  get showVehicleSectionBanner(): boolean {
    return (
      (!!this.showFamilyPlanSuggestion && !this.isSelectedPackageFamilyPlan) ||
      this.headerText !== this.sectionTitle
    );
  }

  get isVehicleInventoryLoading(): boolean {
    return !!this.customerId?.trim() && !this.initialVehiclesFetchDone;
  }

  get showAddVehicleHeaderAction(): boolean {
    return (
      !this.isVehicleInventoryLoading && !this.isAddingForExistingUser && this.canAddMoreVehicles
    );
  }

  addVehicleFormCardTitle(index: number): string {
    return this.vehicleFormSlots.length > 1 ? `Vehicle ${index + 1}` : 'Add Vehicle';
  }

  get addButtonText(): string {
    return this.isFamilyPlanEnabled || this.isSelectedPackageFamilyPlan
      ? 'Add Vehicle (Family Plan)'
      : 'Add Vehicle';
  }

  readonly familyPlanAlertDescription =
    'Adding another vehicle will switch the customer to a family plan.';

  // ── Navigation ─────────────────────────────────────────────────────────────

  /**
   * Handles the continue action by validating the form, ensuring a single vehicle selection, creating pending vehicles, and emitting readiness data when successful.
   * @example
   * onContinue()
   * void
   * @returns {Promise<void>} A promise that resolves when the continue flow completes.
   **/
  async onContinue(): Promise<void> {
    if (!this.isVehicleFormValid) return;
    this.ensureSingleVehicleSelection();
    const success = await this.createPendingVehicles();
    if (!success) return;
    this.vehiclesReady.emit({
      familyVehicleSlots: [...this.familyVehicleSlots],
      vehicleFormSlots: [...this.vehicleFormSlots],
      hasExistingVehicles: this.hasExistingVehicles,
      hasSwitchedToFamilyPlan: this.hasSwitchedToFamilyPlan,
    });
  }

  // ── Vehicle form slot management ───────────────────────────────────────────

  /**
   * Handles adding a new vehicle form slot, with checks for plan and existing-vehicle constraints.
   * @example
   * nAddNewVehicle()
   * void
   * @param {void} - This function does not accept any arguments.
   * @returns {void} Returns nothing.
   **/
  onAddNewVehicle(): void {
    if (!this.canAddMoreVehicles) return;

    const hasVehicles =
      this.hasExistingVehicles ||
      this.vehicleFormSlots.length > 0 ||
      this.familyVehicleSlots.length > 0;
    const hasFamilyPlan = this.isSelectedPackageFamilyPlan || this.hasSwitchedToFamilyPlan;

    if (hasVehicles && hasFamilyPlan && this.isFamilyPlanFromPackage) {
      this.showFamilyPlanAlert = true;
      return;
    }

    if (
      this.hasExistingVehicles &&
      !this.hasSwitchedToFamilyPlan &&
      !this.isFamilyPlanFromPackage
    ) {
      this.isAddingForExistingUser = true;
      this.resetNewVehicleForm();
    } else {
      this.vehicleFormSlots.push(this.emptyFormSlot(this.vehicleFormSlots.length + 1, false));
    }
  }

  onFamilyPlanAlertConfirm(): void {
    this.showFamilyPlanAlert = false;
    this.hasSwitchedToFamilyPlan = true;
    this.vehicleFormSlots.push(this.emptyFormSlot(this.vehicleFormSlots.length + 1, false));
  }

  onFamilyPlanAlertCancel(): void {
    this.showFamilyPlanAlert = false;
  }

  onRemoveVehicleForm(index: number): void {
    if (index >= 0 && index < this.vehicleFormSlots.length && this.vehicleFormSlots.length > 1) {
      this.vehicleFormSlots.splice(index, 1);
      this.vehicleFormSlots.forEach((s, i) => (s.id = i + 1));
    }
  }

  onCancelVehicleForm(index: number): void {
    this.vehicleFormSlots.splice(index, 1);
    this.vehicleFormSlots.forEach((s, i) => (s.id = i + 1));
  }

  /**
   * Saves a vehicle form for the specified slot if it has valid data and has not already been saved.
   * @example
   * nSaveVehicleForm(0)
   * void
   * @param {number} index - Index of the vehicle form slot to save.
   * @returns {void} Nothing is returned.
   **/
  onSaveVehicleForm(index: number): void {
    const slot = this.vehicleFormSlots[index];
    if (!slot?.formData?.vehicleData || !this.customerId) return;
    if (slot.formData.vehicleId) return; // already saved

    slot.isLoading = true;
    this.persistVehicleForSlot(slot).then(ok => {
      slot.isLoading = false;
      if (ok) {
        this.messageService.add({
          severity: 'success',
          summary: 'Vehicle Saved',
          detail: 'Vehicle has been saved successfully.',
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save vehicle.',
        });
      }
    });
  }

  // ── Existing vehicle management ────────────────────────────────────────────

  toggleVehicleSelection(index: number): void {
    this.familyVehicleSlots.forEach((s, i) => {
      s.bookForWash = i === index;
    });
    this.vehicleFormSlots.forEach(s => {
      s.bookForWash = false;
    });
  }

  onVehicleFormBookingChange(isChecked: boolean, index: number): void {
    if (index >= 0 && index < this.vehicleFormSlots.length) {
      if (isChecked) {
        this.vehicleFormSlots.forEach((s, i) => {
          s.bookForWash = i === index;
        });
        this.familyVehicleSlots.forEach(s => {
          s.bookForWash = false;
        });
      } else {
        this.vehicleFormSlots[index].bookForWash = true;
      }
      this.ensureSingleVehicleSelection();
    }
  }

  /**
   * Updates the vehicle form data for a specific slot, merges the provided changes, and manages related selection state.
   * @example
   * nVehicleFormDataChange({ licensePlate: 'ABC123' }, 0)
   * void
   * @param {any} data - Partial form data to merge into the selected vehicle slot.
   * @param {number} [slotIndex] - Index of the vehicle form slot to update.
   * @returns {void} Nothing is returned.
   **/
  onVehicleFormDataChange(data: any, slotIndex?: number): void {
    if (slotIndex === undefined || slotIndex < 0 || slotIndex >= this.vehicleFormSlots.length)
      return;

    const prev = this.vehicleFormSlots[slotIndex].formData;
    const patch = { ...data } as Record<string, any>;
    delete patch['state'];

    const merged: VehicleFormSlot['formData'] = {
      ...prev,
      ...patch,
      selectedState: data?.selectedState ?? data?.state ?? prev.selectedState,
      licensePlate: data?.licensePlate ?? prev.licensePlate,
    };

    this.vehicleFormSlots[slotIndex].formData = merged;
    this.vehicleFormSlots[slotIndex].vehicleData = merged.vehicleData ?? null;

    if (merged.vehicleData) {
      if (!this.vehicleFormSlots.some(s => s.bookForWash)) {
        this.familyVehicleSlots.forEach(s => {
          s.bookForWash = false;
        });
        this.vehicleFormSlots.forEach((s, i) => {
          s.bookForWash = i === slotIndex;
        });
      }
    }
    if (merged.vehicleId) {
      this.vehicleFormSlots[slotIndex].formData.vehicleId = merged.vehicleId;
    }
  }

  /**
   * Updates the vehicle data for a specific vehicle form slot and marks whether the vehicle was fetched.
   * @example
   * nVehicleDataChange(sampleVehicleData, 0)
   * void
   * @param {VehicleData | null} vehicleData - Vehicle data to apply to the selected slot, or null to clear it.
   * @param {number} slotIndex - Index of the vehicle form slot to update.
   * @returns {void} Does not return a value.
   */
  onVehicleDataChange(vehicleData: VehicleData | null, slotIndex: number): void {
    if (slotIndex >= 0 && slotIndex < this.vehicleFormSlots.length) {
      const slot = this.vehicleFormSlots[slotIndex];
      this.onVehicleFormDataChange(
        {
          ...slot.formData,
          vehicleData: vehicleData ?? undefined,
          isVehicleFetched: vehicleData != null,
        },
        slotIndex
      );
    }
  }

  onCustomerDataChange(customer: any): void {
    if (!customer) return;
    this.customerFetched.emit(customer);
  }

  /** Child form already POSTed the vehicle; sync slot id and details for Continue validation. */
  onManualVehicleCreatedForSlot(vehicle: VehicleData & { id?: string }, slotIndex: number): void {
    if (slotIndex < 0 || slotIndex >= this.vehicleFormSlots.length) return;
    const id = (vehicle as any).id ?? '';
    const slot = this.vehicleFormSlots[slotIndex];
    this.onVehicleFormDataChange(
      {
        ...slot.formData,
        vehicleData: { ...vehicle, id },
        vehicleId: id,
        isVehicleFetched: true,
      },
      slotIndex
    );
  }

  onVehicleFormValidityChange(isValid: boolean, slotIndex?: number): void {
    if (slotIndex !== undefined && slotIndex >= 0 && slotIndex < this.vehicleFormSlots.length) {
      this.vehicleFormSlots[slotIndex].isValid = isValid;
    }
  }

  /**
   * Checks whether a vehicle with the same license plate and state already exists in the current form slots or saved vehicle list and shows a warning if it does.
   * @example
   * nCheckDuplicate({ licensePlate: 'ABC123', state: 'CA' }, 0)
   * void
   * @param {{ licensePlate: string; state: string }} vehicleInfo - Vehicle information containing the license plate and state to validate.
   * @param {number} slotIndex - Index of the current vehicle slot to exclude from duplicate checking.
   * @returns {void} Does not return a value.
   */
  onCheckDuplicate(vehicleInfo: { licensePlate: string; state: string }, slotIndex: number): void {
    const plate = vehicleInfo.licensePlate.trim().toLowerCase();
    const state = vehicleInfo.state.trim().toLowerCase();

    const duplicateInSlots = this.vehicleFormSlots.some((s, i) => {
      if (i === slotIndex || !s.formData?.licensePlate) return false;
      return (
        s.formData.licensePlate.trim().toLowerCase() === plate &&
        s.formData.selectedState?.trim().toLowerCase() === state
      );
    });

    const duplicateInExisting = this.familyVehicleSlots.some(s => {
      const p = (s.vehicleData?.plate ?? s.licensePlate).trim().toLowerCase();
      const st = (s.vehicleData?.state ?? s.state).trim().toLowerCase();
      return p === plate && st === state;
    });

    if (duplicateInSlots || duplicateInExisting) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Duplicate Vehicle',
        detail: 'This vehicle is already added. Please use a different license plate.',
      });
    }
  }

  // ── Edit flow ──────────────────────────────────────────────────────────────

  editVehicle(vehicle: FamilyVehicleSlot, index: number): void {
    const emptyVehicleData: VehicleData = {
      status: 'active',
      vin: '',
      year: new Date().getFullYear(),
      make: '',
      model: '',
      state: vehicle.state ?? '',
      plate: vehicle.licensePlate ?? '',
      _utc: '',
      last_updated: '',
      cached: '',
    };
    this.editingVehicleData = {
      id: vehicle.id.toString(),
      licensePlate: vehicle.licensePlate ?? '',
      state: vehicle.state ?? '',
      vehicleData: vehicle.vehicleData ?? emptyVehicleData,
    };
    this.isEditingVehicle = true;
    this.vehicleToEditIndex = index;
  }

  onCancelEdit(): void {
    this.editingVehicleData = null;
    this.isEditingVehicle = false;
    this.vehicleToEditIndex = null;
  }

  /**
   * Updates the currently edited vehicle with the provided data and syncs the local slot state after a successful save.
   * @example
   * nSaveEdit(updatedData)
   * void
   * @param {any} updatedData - The edited vehicle data containing the vehicle ID, license plate, state, and vehicle details.
   * @returns {void} No return value.
   **/
  onSaveEdit(updatedData: EditVehicleData): void {
    if (this.vehicleToEditIndex === null || !this.customerId || !updatedData) return;
    this.isLoading = true;

    const payload = {
      license_plate: updatedData.licensePlate,
      state: updatedData.state,
      vin: updatedData.vehicleData?.vin ?? '',
      make: updatedData.vehicleData?.make ?? '',
      model: updatedData.vehicleData?.model ?? '',
      year: updatedData.vehicleData?.year ?? 0,
    };

    this.vehicleService
      .updateVehicle(updatedData.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const slot = this.familyVehicleSlots[this.vehicleToEditIndex!];
          if (slot) {
            slot.vehicleData = {
              ...(slot.vehicleData ?? ({} as VehicleData)),
              ...updatedData.vehicleData,
              plate: updatedData.licensePlate,
              state: updatedData.state,
            };
            slot.licensePlate = updatedData.licensePlate;
            slot.state = updatedData.state;
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Vehicle Updated',
            detail: 'Vehicle details have been updated successfully.',
          });
          this.onCancelEdit();
          this.isLoading = false;
        },
        error: err => {
          this.isLoading = false;
          this.logger.trackError('VehicleStep.saveEdit', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update vehicle.',
          });
        },
      });
  }

  // ── Delete flow ────────────────────────────────────────────────────────────

  deleteVehicle(vehicle: any, index: number): void {
    this.vehicleToDelete = vehicle;
    this.vehicleToDeleteIndex = index;
    const total =
      this.familyVehicleSlots.length +
      this.vehicleFormSlots.filter(s => s.formData?.vehicleId).length;
    const onFamily = this.isSelectedPackageFamilyPlan || this.hasSwitchedToFamilyPlan;
    this.isDeletingToSwitchToIndividualPlan = onFamily && total === 2;
    this.showDeleteModal = true;
  }

  /**
   * Deletes the selected vehicle from the family vehicle list and updates related UI state.
   * @example
   * nDeleteModalConfirm(vehicle)
   * void
   * @param {any} vehicle - The vehicle object selected for deletion.
   * @returns {void} No return value.
   **/
  onDeleteModalConfirm(vehicle: any): void {
    if (!vehicle || this.vehicleToDeleteIndex === null) return;
    this.isLoading = true;

    this.vehicleService
      .deleteVehicle(vehicle.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.familyVehicleSlots.splice(this.vehicleToDeleteIndex!, 1);
          this.familyPlanVehicleCount = this.familyVehicleSlots.length;
          this.hasExistingVehicles = this.familyVehicleSlots.length > 0;
          if (this.isDeletingToSwitchToIndividualPlan) this.hasSwitchedToFamilyPlan = false;
          if (!this.hasExistingVehicles && this.vehicleFormSlots.length === 0) this.initSlots();
          this.ensureSingleVehicleSelection();
          const msg = this.isDeletingToSwitchToIndividualPlan
            ? 'Vehicle removed. Customer switched back to individual plan.'
            : 'Vehicle removed successfully.';
          this.messageService.add({ severity: 'success', summary: 'Vehicle Removed', detail: msg });
          this.showDeleteModal = false;
          this.vehicleToDelete = null;
          this.vehicleToDeleteIndex = null;
          this.isLoading = false;
        },
        error: err => {
          this.isLoading = false;
          this.logger.trackError('VehicleStep.deleteVehicle', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to remove vehicle.',
          });
        },
      });
  }

  onDeleteModalCancel(): void {
    this.showDeleteModal = false;
    this.vehicleToDelete = null;
    this.vehicleToDeleteIndex = null;
  }

  // ── Add vehicle for existing user ──────────────────────────────────────────

  onCancelAddVehicle(): void {
    this.isAddingForExistingUser = false;
    this.resetNewVehicleForm();
  }

  /**
   * Save a looked-up vehicle for an existing customer (CWS-1398).
   *
   * The plate lookup no longer stores what it finds, so a fetched vehicle needs an
   * explicit save — the manual-entry path POSTs from inside the form, this one did not.
   * Mirrors onSaveVehicleForm() for the slot forms, and hands the created vehicle to the
   * same handler so the family list and toast behave identically.
   */
  onSaveNewVehicleForExistingCustomer(): void {
    const vehicleData = this.newVehicleForm.vehicleData;
    if (!vehicleData || !this.customerId || this.newVehicleForm.isLoading) return;

    this.newVehicleForm.isLoading = true;
    const payload = this.buildVehiclePayload(this.newVehicleForm, vehicleData);
    this.createVehicleViaApi(payload, id => {
      this.onNewVehicleCreatedForExistingCustomer({ ...vehicleData, id });
    }).then(created => {
      // On success the handler above resets the form; only the failure path needs this.
      if (!created) {
        this.newVehicleForm.isLoading = false;
      }
    });
  }

  /**
   * Add-vehicle form already created the vehicle via API; append to family list (avoid double POST).
   */
  onNewVehicleCreatedForExistingCustomer(vehicle: VehicleData & { id?: string }): void {
    const id = (vehicle as any).id;
    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Vehicle was saved but no ID was returned.',
      });
      return;
    }
    this.newVehicleForm.isLoading = false;
    this.familyVehicleSlots.push({
      id,
      state: vehicle.state ?? this.newVehicleForm.selectedState,
      licensePlate: vehicle.plate ?? this.newVehicleForm.licensePlate,
      vehicleData: { ...vehicle, id },
      isLoading: false,
      errorMessage: '',
      bookForWash: false,
    });
    this.familyPlanVehicleCount = this.familyVehicleSlots.length;
    this.ensureSingleVehicleSelection();
    this.isAddingForExistingUser = false;
    this.resetNewVehicleForm();
    this.messageService.add({
      severity: 'success',
      summary: 'Vehicle Added',
      detail: 'Vehicle added successfully.',
    });
  }

  // ── Helpers for template ───────────────────────────────────────────────────

  /**
   * Builds a vehicle item data object from a family vehicle slot.
   * @example
   * oVehicleItemData(slot)
   * { id: 1, year: 2020, make: 'Toyota', model: 'Camry', licensePlate: 'ABC123', vin: '1HGCM82633A004352', selected: true }
   * @param {FamilyVehicleSlot} slot - The family vehicle slot to convert into vehicle item data.
   * @returns {VehicleItemData} The mapped vehicle item data object.
   **/
  toVehicleItemData(slot: FamilyVehicleSlot): VehicleItemData {
    const v = slot.vehicleData;
    return {
      id: slot.id,
      year: v?.year ?? 0,
      make: v?.make ?? '',
      model: v?.model ?? '',
      licensePlate: v?.plate ?? slot.licensePlate,
      vin: v?.vin ?? '',
      selected: slot.bookForWash,
    };
  }

  trackBySlotId(_: number, slot: FamilyVehicleSlot | VehicleFormSlot): string | number {
    return slot.id;
  }

  // ── Private ────────────────────────────────────────────────────────────────

  /**
   * Fetches existing vehicles for the current customer and initializes vehicle slots accordingly.
   * @example
   * fetchExistingVehicles()
   * void
   * @returns {void} This function does not return a value.
   */
  private fetchExistingVehicles(): void {
    if (!this.customerId?.trim()) {
      this.hasExistingVehicles = false;
      this.familyVehicleSlots = [];
      this.initSlots();
      this.ensureSingleVehicleSelection();
      this.initialVehiclesFetchDone = true;
      return;
    }
    this.vehicleService
      .getVehiclesByCustomerId(this.customerId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.initialVehiclesFetchDone = true;
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res.status && Array.isArray(res.data) && res.data.length > 0) {
            this.hasExistingVehicles = true;
            this.familyVehicleSlots = res.data.map((v: any, i: number) => ({
              id: v.id ?? v.vehicle_id,
              state: v.state ?? '',
              licensePlate: v.license_plate ?? v.licensePlate ?? '',
              vehicleData: {
                id: v.id ?? v.vehicle_id,
                status: v.status ?? 'active',
                vin: v.vin ?? '',
                year: v.year ?? new Date().getFullYear(),
                make: v.make ?? '',
                model: v.model ?? '',
                state: v.state ?? '',
                plate: v.license_plate ?? v.licensePlate ?? '',
                _utc: v._utc ?? new Date().toISOString(),
                last_updated: v.last_updated ?? new Date().toISOString(),
                cached: v.cached ?? new Date().toISOString(),
              },
              isLoading: false,
              errorMessage: '',
              bookForWash: i === 0,
            }));
            this.familyPlanVehicleCount = res.data.length;
          } else {
            this.hasExistingVehicles = false;
            this.familyVehicleSlots = [];
          }
          this.initSlots();
          this.ensureSingleVehicleSelection();
        },
        error: () => {
          this.hasExistingVehicles = false;
          this.familyVehicleSlots = [];
          this.initSlots();
          this.ensureSingleVehicleSelection();
        },
      });
  }

  private initSlots(): void {
    if (!this.hasExistingVehicles) {
      this.vehicleFormSlots = [this.emptyFormSlot(1, true)];
    } else {
      this.vehicleFormSlots = [];
    }
  }

  /**
   * Restores the vehicle step component's local state from the shared state service and initializes slots when needed.
   * @example
   * restoreFromState()
   * void
   * @returns {void} Does not return a value.
   */
  private restoreFromState(): void {
    if (!this.stateService) return;
    const s = this.stateService;
    this.familyVehicleSlots = [...s.familyVehicleSlots];
    this.vehicleFormSlots = [...s.vehicleFormSlots];
    this.hasExistingVehicles = s.hasExistingVehicles;
    this.isFamilyPlanEnabled = s.isFamilyPlanEnabled;
    this.hasSwitchedToFamilyPlan = s.hasSwitchedToFamilyPlan;
    this.familyPlanVehicleCount = s.familyPlanVehicleCount;
    if (this.vehicleFormSlots.length === 0 && this.familyVehicleSlots.length === 0) {
      if (!this.customerId?.trim()) {
        this.initSlots();
      }
    }
  }

  /**
   * Ensures only one vehicle can be selected for wash across existing family vehicles and newly added vehicle forms.
   * @example
   * ensureSingleVehicleSelection()
   * undefined
   * @returns {void} Does not return a value.
   **/
  private ensureSingleVehicleSelection(): void {
    const selectedExisting = this.familyVehicleSlots.filter(s => s.bookForWash);
    if (selectedExisting.length > 1) {
      const first = this.familyVehicleSlots.findIndex(s => s.bookForWash);
      this.familyVehicleSlots.forEach((s, i) => {
        s.bookForWash = i === first;
      });
    }

    const selectedForms = this.vehicleFormSlots.filter(s => s.bookForWash);
    if (selectedForms.length > 1) {
      const first = this.vehicleFormSlots.findIndex(s => s.bookForWash);
      this.vehicleFormSlots.forEach((s, i) => {
        s.bookForWash = i === first;
      });
    }

    if (selectedExisting.length > 0) {
      this.vehicleFormSlots.forEach(s => {
        s.bookForWash = false;
      });
    }

    const anySelected =
      this.familyVehicleSlots.some(s => s.bookForWash) ||
      this.vehicleFormSlots.some(s => s.bookForWash);
    if (!anySelected) {
      if (this.familyVehicleSlots.length > 0) this.familyVehicleSlots[0].bookForWash = true;
      else if (this.vehicleFormSlots.length > 0) this.vehicleFormSlots[0].bookForWash = true;
    }
  }

  private async createPendingVehicles(): Promise<boolean> {
    if (!this.customerId) return true;

    for (const slot of this.vehicleFormSlots) {
      if (slot.formData?.vehicleData && !slot.formData.vehicleId) {
        const ok = await this.persistVehicleForSlot(slot);
        if (!ok) return false;
      }
    }
    return true;
  }

  /**
   * Persists a vehicle for a booking slot by updating an existing vehicle or creating a new one.
   * @example
   * persistVehicleForSlot(slot)
   * true
   * @param {VehicleFormSlot} slot - Slot containing form data and vehicle information to persist.
   * @returns {Promise<boolean>} Resolves to true when the vehicle is successfully persisted, otherwise false.
   **/
  private async persistVehicleForSlot(slot: VehicleFormSlot): Promise<boolean> {
    if (!slot.formData?.vehicleData) return false;
    const existingVehicleId = (
      (slot.formData.vehicleData as any).id ??
      (slot.formData.vehicleData as any).vehicle_id ??
      ''
    ).toString();

    // AskAuto/lookup flow: vehicle already exists in DB. Attach created customer to that vehicle.
    if (existingVehicleId) {
      const payload = this.buildVehiclePayload(slot.formData, slot.formData.vehicleData);
      return this.updateVehicleViaApi(existingVehicleId, payload, id => {
        slot.formData.vehicleId = id;
      });
    }

    // Fallback: manually-created/new vehicle with no existing ID.
    const payload = this.buildVehiclePayload(slot.formData, slot.formData.vehicleData);
    return this.createVehicleViaApi(payload, id => {
      slot.formData.vehicleId = id;
    });
  }

  /**
   * Creates a vehicle through the API and handles success and error responses.
   * @example
   * createVehicleViaApi(payload, onSuccess)
   * true
   * @param {any} payload - Vehicle data payload to send to the API.
   * @param {(id: string) => void} onSuccess - Callback invoked with the created vehicle ID on successful creation.
   * @returns {Promise<boolean>} Promise that resolves to true when the vehicle is created successfully, otherwise false.
   **/
  private createVehicleViaApi(payload: any, onSuccess: (id: string) => void): Promise<boolean> {
    return new Promise(resolve => {
      this.vehicleService.createVehicle(payload).subscribe({
        next: (res: any) => {
          if (res.status && res.data?.id) {
            onSuccess(res.data.id);
            resolve(true);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: res.message || 'Failed to create vehicle.',
            });
            resolve(false);
          }
        },
        error: err => {
          this.logger.trackError('VehicleStep.createVehicle', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.extractErrorMessage(err, 'Failed to create vehicle.'),
          });
          resolve(false);
        },
      });
    });
  }

  /**
   * Updates a vehicle through the API and handles success or error responses.
   * @example
   * updateVehicleViaApi(vehicleId, payload, onSuccess)
   * true
   * @param {string} vehicleId - The ID of the vehicle to update.
   * @param {any} payload - The update payload to send to the vehicle API.
   * @param {(id: string) => void} onSuccess - Callback invoked when the update succeeds.
   * @returns {Promise<boolean>} A promise that resolves to true when the update succeeds, otherwise false.
   **/
  private updateVehicleViaApi(
    vehicleId: string,
    payload: any,
    onSuccess: (id: string) => void
  ): Promise<boolean> {
    return new Promise(resolve => {
      this.vehicleService.updateVehicle(vehicleId, payload).subscribe({
        next: (res: any) => {
          if (res.status) {
            onSuccess(vehicleId);
            resolve(true);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: res.message || 'Failed to update vehicle.',
            });
            resolve(false);
          }
        },
        error: err => {
          this.logger.trackError('VehicleStep.updateVehicle', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.extractErrorMessage(err, 'Failed to update vehicle.'),
          });
          resolve(false);
        },
      });
    });
  }

  /**
   * Builds the vehicle payload object by combining form data, vehicle data, and component state.
   * @example
   * buildVehiclePayload(formData, vehicleData)
   * { license_plate: 'ABC123', state: 'CA', vin: '1HGCM82633A123456', make: 'Honda', model: 'Civic', year: 2020, customer_id: 42, is_family_plan: false }
   * @param {any} formData - Form values containing optional license plate and state selections.
   * @param {VehicleData} vehicleData - Vehicle details used as fallback and source of core vehicle information.
   * @returns {any} A normalized payload object ready for vehicle-related requests.
   **/
  private buildVehiclePayload(formData: any, vehicleData: VehicleData): any {
    return {
      license_plate: formData.licensePlate ?? vehicleData.plate,
      state: formData.selectedState ?? vehicleData.state,
      vin: vehicleData.vin ?? '',
      make: vehicleData.make ?? '',
      model: vehicleData.model ?? '',
      year:
        typeof vehicleData.year === 'string' ? parseInt(vehicleData.year) : (vehicleData.year ?? 0),
      customer_id: this.customerId,
      is_family_plan: this.isFamilyPlanEnabled || this.isSelectedPackageFamilyPlan,
    };
  }

  private resetNewVehicleForm(): void {
    this.newVehicleForm = {
      selectedState: '',
      licensePlate: '',
      vehicleData: null,
      isValid: false,
      isLoading: false,
      errorMessage: '',
    };
  }

  private emptyFormSlot(id: number, bookForWash: boolean): VehicleFormSlot {
    return {
      id,
      formData: { selectedState: '', licensePlate: '', vehicleId: '' },
      vehicleData: null,
      isValid: false,
      isLoading: false,
      errorMessage: '',
      bookForWash,
    };
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.status === 409 || err?.error?.status_code === 409)
      return err.error?.message ?? 'Vehicle already exists.';
    return err?.error?.message ?? err?.message ?? fallback;
  }
}
